import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import { capitalize, find, groupBy, map } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormSelect,
} from '../../../common/components/sections'
import FormLazyTreeSelect from '../../../common/components/sections/form-lazy-tree-select'
import { CHALLENGE_TYPES, PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  optionPropType,
  simpleEntityPropType,
} from '../../../common/prop-types'
import { flattenTreeStructure } from '../../../common/utils'

import { useUpdateChallengeRelationsDetailsMutation } from '../../hooks'
import { challengePropType } from '../../prop-types'
import { setChallengePageIsDirty } from '../../redux/actions'

import { challengeRelationsDetailsFormInitialValues } from './initial-values'

const ChallengeRelationsDetailsForm = ({
  challenge,
  dishTypeOptions,
  cuisineTypeOptions,
}) => {
  const [challengeType, setChallengeType] = useState(challenge.type)
  const dispatch = useDispatch()

  const challengeTypeOptions = useMemo(
    () =>
      map(CHALLENGE_TYPES, challengeType => ({
        value: challengeType,
        label: capitalize(challengeType).replace(/_/g, ' '),
      })),
    [],
  )

  const flattenedDishTypeOptions = useMemo(
    () => flattenTreeStructure(dishTypeOptions),
    [dishTypeOptions],
  )

  const groupedDishTypeFlatTreeDataByValue = useMemo(
    () => groupBy(flattenedDishTypeOptions, 'value'),
    [flattenedDishTypeOptions],
  )

  const flattenedCuisineTypeOptions = useMemo(
    () => flattenTreeStructure(cuisineTypeOptions),
    [cuisineTypeOptions],
  )

  const groupedCuisineFlatTreeDataByValue = useMemo(
    () => groupBy(flattenedCuisineTypeOptions, 'value'),
    [flattenedCuisineTypeOptions],
  )

  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.CHALLENGE.SAVE_RELATIONS,
  ])

  const [
    updateChallengeIncludedChallengesDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateChallengeRelationsDetailsMutation()

  const initialValues = useMemo(
    () => challengeRelationsDetailsFormInitialValues(challenge),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [challenge.type, challenge.dishTypes, challenge.cuisineTypes],
  )

  const handleSaveData = useCallback(
    data => updateChallengeIncludedChallengesDetails(challenge.id, data),
    [challenge.id, updateChallengeIncludedChallengesDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Challenge details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
    setChallengeType(initialValues.type)
  }, [initialValues, resetForm])

  useEffect(() => {
    dispatch(setChallengePageIsDirty({ isRelationsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="relationsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={changedValues => {
        setToDirty()
        const changedType = find(
          changedValues,
          ({ name }) => name[0] === 'type',
        )?.value

        if (changedType) {
          setChallengeType(changedType)
        }
      }}
    >
      <Row>
        <Col>
          <FormSelect
            disabled={!isFormEnabled}
            name="type"
            labelAbove
            label="Challenge type (this will remove all previously linked items"
            showSearch={false}
            allowClear={false}
            options={challengeTypeOptions}
            placeholder="Select type..."
          />
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          {challengeType === CHALLENGE_TYPES.CUISINE_TYPE ? (
            <FormLazyTreeSelect
              disabled={!isFormEnabled}
              form={form}
              name="cuisineTypes"
              label="Cuisine Types"
              showSearch
              labelAbove
              allowClear
              treeCheckable
              treeCheckStrictly
              treeData={cuisineTypeOptions}
              placeholder="Select cuisine types..."
              groupedFlatTreeDataByValue={groupedCuisineFlatTreeDataByValue}
              flatTreeData={flattenedCuisineTypeOptions}
              initialValue={initialValues.cuisineTypes}
            />
          ) : (
            <FormLazyTreeSelect
              disabled={!isFormEnabled}
              form={form}
              name="dishTypes"
              label="Dish Types"
              showSearch
              labelAbove
              allowClear
              treeCheckable
              treeCheckStrictly
              treeData={dishTypeOptions}
              placeholder="Select dish types..."
              groupedFlatTreeDataByValue={groupedDishTypeFlatTreeDataByValue}
              flatTreeData={flattenedDishTypeOptions}
              initialValue={initialValues.dishTypes}
            />
          )}
        </Col>
      </Row>

      <FormButtons
        permissions={[PERMISSIONS.CHALLENGE.SAVE_RELATIONS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

ChallengeRelationsDetailsForm.propTypes = {
  challenge: challengePropType.isRequired,
  dishTypeOptions: PropTypes.arrayOf(simpleEntityPropType).isRequired,
  cuisineTypeOptions: PropTypes.arrayOf(optionPropType.isRequired).isRequired,
}

export default ChallengeRelationsDetailsForm
